import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
} from '@mui/material';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IUnitListItem } from 'src/types/unit';
import {
  IKitchenTableFilters,
  IKitchenTableFilterValue,
  KITCHEN_ALERT_OPTIONS,
} from 'src/types/kitchen';

type Props = {
  filters: IKitchenTableFilters;
  onFilters: (name: string, value: IKitchenTableFilterValue) => void;
  units: IUnitListItem[];
  handleResetFilters: VoidFunction;
  canReset: boolean;
  activeFilterCount: number;
};

const ALERT_STATUS_OPTIONS = [{ value: 'all', label: 'All' }, ...KITCHEN_ALERT_OPTIONS];

export default function KitchenTableToolbar({
  filters,
  onFilters,
  units,
  handleResetFilters,
  canReset,
  activeFilterCount,
}: Props) {
  const [localMinQuantity, setLocalMinQuantity] = useState('');
  const [localMaxQuantity, setLocalMaxQuantity] = useState('');
  const [input, setInput] = useState<string>('');

  const filtersPopover = usePopover();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      onFilters('generalSearch', value);
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMinQuantity = useCallback(
    debounce((value) => {
      onFilters('minQuantity', value);
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedMaxQuantity = useCallback(
    debounce((value) => {
      onFilters('maxQuantity', value);
    }, 500),
    []
  );

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedMinQuantity.cancel();
      debouncedMaxQuantity.cancel();
    };
  }, [debouncedSearch, debouncedMinQuantity, debouncedMaxQuantity]);

  const handleFilterUnits = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters('units', event.target.value);
    },
    [onFilters]
  );

  const handleFilterAlerts = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters('alertsEnabled', event.target.value);
    },
    [onFilters]
  );

  const handleFilterExpiringSoon = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('expiringSoon', event.target.checked);
    },
    [onFilters]
  );

  const handleFilterExpired = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFilters('expired', event.target.checked);
    },
    [onFilters]
  );

  const handleMinQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setLocalMinQuantity(value);

    debouncedMinQuantity(value === '' ? null : value);
  };

  const handleMaxQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setLocalMaxQuantity(value);

    debouncedMaxQuantity(value === '' ? null : value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setInput(value);

    debouncedSearch(value);
  };

  const clearSearchText = () => {
    setInput('');

    debouncedSearch('');
  };

  const clearAll = useCallback(() => {
    setInput('');
    setLocalMinQuantity('');
    setLocalMaxQuantity('');
    handleResetFilters();
  }, [handleResetFilters]);

  const hasQuantityError =
    Number(localMaxQuantity) < Number(localMinQuantity) && localMaxQuantity !== '';
  const hasNegativeMinQuantity = Number(localMinQuantity) < 0;
  const hasNegativeMaxQuantity = Number(localMaxQuantity) < 0;

  return (
    <>
      <Stack
        spacing={2}
        alignItems="center"
        direction="row"
        sx={{
          p: 2.5,
        }}
      >
        <TextField
          fullWidth
          value={input}
          onChange={handleSearchChange}
          placeholder="Search for item name, item code, barcode..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment:
              input.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearchText}>
                    <Iconify icon="eva:close-fill" color="text.disabled" />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          inputProps={{
            autoComplete: 'off',
          }}
        />

        <Tooltip title="Filters">
          <Badge color="error" badgeContent={activeFilterCount} invisible={activeFilterCount === 0}>
            <IconButton
              onClick={filtersPopover.onOpen}
              color={filtersPopover.open ? 'inherit' : 'default'}
            >
              <Iconify icon="ic:round-filter-list" />
            </IconButton>
          </Badge>
        </Tooltip>

        {canReset && (
          <Button
            color="error"
            onClick={clearAll}
            variant="text"
            sx={{ textDecoration: 'underline', flexShrink: 0 }}
          >
            Clear
          </Button>
        )}
      </Stack>

      <CustomPopover
        open={filtersPopover.open}
        onClose={filtersPopover.onClose}
        arrow="top-right"
        sx={{ width: 320 }}
      >
        <Stack spacing={2.5} sx={{ p: 2.5 }}>
          <Typography variant="subtitle2">Filters</Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Min Quantity"
              value={localMinQuantity}
              onChange={handleMinQuantityChange}
              type="number"
              placeholder="Min Quantity"
              fullWidth
              inputProps={{
                min: 0,
                autoComplete: 'off',
              }}
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              error={hasNegativeMinQuantity}
              InputProps={{
                endAdornment: hasNegativeMinQuantity ? (
                  <InputAdornment position="end">
                    <Tooltip title="Min quantity cannot be negative">
                      <Iconify icon="eva:info-outline" color="error.main" />
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            <TextField
              label="Max Quantity"
              value={localMaxQuantity}
              onChange={handleMaxQuantityChange}
              type="number"
              placeholder="Max Quantity"
              fullWidth
              inputProps={{
                min: 0,
                autoComplete: 'off',
              }}
              error={hasQuantityError || hasNegativeMaxQuantity}
              InputProps={{
                endAdornment:
                  hasQuantityError || hasNegativeMaxQuantity ? (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          hasNegativeMaxQuantity
                            ? 'Max Quantity cannot be negative'
                            : 'Max Quantity should be greater than or equal to Min Quantity'
                        }
                      >
                        <Iconify icon="eva:info-outline" color="error.main" />
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
              }}
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>Units</InputLabel>

            <Select
              multiple
              value={filters.units}
              onChange={handleFilterUnits}
              input={<OutlinedInput label="Units" />}
              renderValue={(selected) => (
                <Label variant="soft" color="primary">
                  Units {selected.length < 10 ? `0${selected.length}` : selected.length}
                </Label>
              )}
              sx={{ textTransform: 'capitalize' }}
            >
              {units.map((option) => (
                <MenuItem key={option._id} value={option._id}>
                  <Checkbox
                    disableRipple
                    size="small"
                    checked={filters.units.includes(option._id)}
                  />
                  {option.abbreviation}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Alerts</InputLabel>

            <Select
              multiple={false}
              value={[filters.alertsEnabled]}
              onChange={handleFilterAlerts}
              input={<OutlinedInput label="Alerts" />}
              renderValue={(selected) => (
                <Label variant="soft" color="primary">
                  {selected}
                </Label>
              )}
              sx={{ textTransform: 'capitalize' }}
            >
              {ALERT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <FormControlLabel
            control={<Switch checked={filters.expiringSoon} onChange={handleFilterExpiringSoon} />}
            label="Expiring Soon"
            sx={{ mx: 0 }}
          />

          <FormControlLabel
            control={
              <Switch checked={filters.expired} onChange={handleFilterExpired} color="error" />
            }
            label="Already Expired"
            sx={{ mx: 0 }}
          />
        </Stack>
      </CustomPopover>
    </>
  );
}
